import Vue from 'vue';
import Vuex from 'vuex';
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  signInWithEmailAndPassword,
  signInWithCustomToken,
} from 'firebase/auth';
import VuexPersist from 'vuex-persist';
import enrollment from './enrollment';
import { findError } from '../constants/errors';
import common from './common';
import snackbar from './snackbar';
import versionUpdate from './versionUpdate';
import firebaseService from '../services/firebase.service';

Vue.use(Vuex);

async function getBackendV2Service() {
  // eslint-disable-next-line import/no-cycle
  const module = await import('../services/backend-v2.service');
  return module.default;
}

async function tryCreateSession(idToken: string | undefined): Promise<void> {
  if (!idToken) return;
  await getBackendV2Service()
    .then((service) => service.createSession(idToken))
    .catch((error) => {
      console.error('Failed to create session cookie:', error);
    });
}

const vuexPersist = new VuexPersist({
  // Al cambiar esta llave debe cambiarse también en "@/utils/request" para poder obtener el token
  key: 'beereaders-enrollment-admin',
  storage: window.localStorage,
  reducer: (state: {
    currentUser: any,
    userLogged: any,
    common: any,
    enrollment: any,
    userToken: any,
    registerSuccess: any,
    loginError: any,
    registerError: any,
    loading: any,
    showSchoolGroupDialog: any,
    districtAdmins: any,
    districtAdminsTotalPages: any,
    campusGroupAdmins: any,
    campusGroupAdminsTotalPages: any,
    directiveAdmins: any,
    directiveAdminsTotalPages: any,
  }) => ({
    currentUser: state.currentUser,
    userLogged: state.userLogged,
    common: state.common,
    enrollment: state.enrollment,
    userToken: state.userToken,
  }),
});

export default new Vuex.Store({
  state: {
    currentUser: null,
    userLogged: false,
    common: {},
    enrollment: {},
    registerSuccess: null,
    loginError: null,
    registerError: null,
    loading: false,
    userToken: null,
    showSchoolGroupDialog: false,
    districtAdmins: [],
    districtAdminsTotalPages: 0,
    campusGroupAdmins: [],
    campusGroupAdminsTotalPages: 0,
    directiveAdmins: [],
    directiveAdminsTotalPages: 0,
  },
  mutations: {
    SET_CURRENT_USER(state: any, payload: any) {
      state.currentUser = { ...payload };
    },
    SET_USER_TOKEN(state: any, payload: any) {
      state.userToken = payload;
    },
    SET_USER_LOGGED(state: any, payload: any) {
      state.userLogged = payload;
    },
    SET_REGISTER_SUCCESS(state: any, payload: any) {
      state.registerSuccess = payload;
    },
    SET_LOGIN_ERROR(state: any, payload: any) {
      state.loginError = payload;
    },
    SET_REGISTER_ERROR(state: any, payload: any) {
      state.registerError = payload;
    },
    SET_LOADING(state: any, payload: boolean) {
      state.loading = payload;
    },
    SHOW_SCHOOL_GROUP_DIALOG(state: any, payload: boolean) {
      state.showSchoolGroupDialog = payload;
    },
    SET_DISTRICT_ADMINS(state: any, payload: any[]) {
      state.districtAdmins = payload;
    },
    SET_DISTRICT_ADMINS_TOTAL_PAGES(state: any, payload: number) {
      state.districtAdminsTotalPages = payload;
    },
    SET_CAMPUS_GROUP_ADMINS(state: any, payload: any[]) {
      state.campusGroupAdmins = payload;
    },
    SET_CAMPUS_GROUP_ADMINS_TOTAL_PAGES(state: any, payload: number) {
      state.campusGroupAdminsTotalPages = payload;
    },
    SET_DIRECTIVE_ADMINS(state: any, payload: any[]) {
      state.directiveAdmins = payload;
    },
    SET_DIRECTIVE_ADMINS_TOTAL_PAGES(state: any, payload: number) {
      state.directiveAdminsTotalPages = payload;
    },
  },
  actions: {
    async register(
      { commit },
      {
        name,
        surname,
        email,
        password,
        schoolGroupId,
      },
    ) {
      commit('SET_LOADING', true);
      const auth = getAuth();
      await firebaseService.registerUser({
        displayName: `${name} ${surname}`,
        email,
        password,
        schoolGroupId,
        fromGoogle: false,
      })
        .then(async (user) => {
          await signInWithCustomToken(auth, user.token);
          return user;
        })
        .then(async (user) => {
          // Get Firebase ID token for backend authentication
          const idToken = await auth.currentUser?.getIdToken();
          commit('SET_CURRENT_USER', user);
          commit('SET_USER_LOGGED', user !== null);
          commit('SET_REGISTER_SUCCESS', true);
          commit('SET_USER_TOKEN', idToken);
          await tryCreateSession(idToken);
        })
        .catch((error) => {
          const errorCode = error.code;
          const errorMessage = error.message;
          const errorKey = findError(errorCode);
          commit('SET_REGISTER_ERROR', { errorCode, errorMessage, error: errorKey?.key });
          commit('SET_USER_LOGGED', false);
          commit('SET_CURRENT_USER', null);
        });
      commit('SET_LOADING', false);
    },
    async signInCredentials({ commit }, { email, password }) {
      commit('SET_LOADING', true);
      const auth = getAuth();
      await signInWithEmailAndPassword(auth, email, password)
        .then(() => firebaseService.getUser(email))
        .then(async (user) => {
          let newUser = user;
          // If the user does not exist in the backend, we register them like the Google flow
          if (!user) {
            newUser = await firebaseService.registerUser({
              displayName: auth.currentUser?.displayName || email,
              email,
              fromGoogle: false,
            });
          }
          // Get Firebase ID token for backend authentication
          const idToken = await auth.currentUser?.getIdToken();
          commit('SET_CURRENT_USER', newUser);
          commit('SET_USER_LOGGED', newUser !== null);
          commit('SET_USER_TOKEN', idToken);
          await tryCreateSession(idToken);
        })
        .catch((error) => {
          const errorCode = error.code;
          const errorMessage = error.message;
          const errorKey = findError(errorCode);
          commit('SET_LOGIN_ERROR', { errorCode, errorMessage, error: errorKey?.key });
          commit('SET_USER_LOGGED', false);
          commit('SET_CURRENT_USER', null);
        });
      commit('SET_LOADING', false);
    },
    async signIn({ commit, dispatch }) {
      commit('SET_LOADING', true);
      const auth = getAuth();
      const provider = new GoogleAuthProvider();
      provider.addScope('https://www.googleapis.com/auth/userinfo.email');
      await signInWithPopup(auth, provider)
        .then(async (result) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const firebaseUser = await firebaseService.getUser(result.user.email!);
          if (firebaseUser) {
            return {
              firebaseUser,
              result,
            };
          }

          await dispatch('signOut');
          const registeredUser = await firebaseService.registerUser({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            displayName: result.user.displayName!,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            email: result.user.email!,
            fromGoogle: true,
          });
          return {
            firebaseUser: registeredUser,
            result,
          };
        })
        .then(async ({ result, firebaseUser }) => {
          // Get Firebase ID token for backend authentication (not Google access token)
          const idToken = await result.user.getIdToken();
          commit('SET_CURRENT_USER', firebaseUser);
          commit('SET_USER_LOGGED', firebaseUser !== null);
          commit('SET_USER_TOKEN', idToken);
          await tryCreateSession(idToken);
        }).catch((error) => {
          // Handle Errors here.
          const errorCode = error.code;
          const errorMessage = error.message;
          // The AuthCredential type that was used.
          const credential = GoogleAuthProvider.credentialFromError(error);
          commit('SET_LOGIN_ERROR', { errorCode, errorMessage, error: credential });
          commit('SET_USER_LOGGED', false);
          commit('SET_CURRENT_USER', null);
        });
      commit('SET_LOADING', false);
    },
    async signOut({ commit }) {
      // Clear backend session cookie before Firebase sign out (best-effort)
      await getBackendV2Service()
        .then((service) => service.clearSession())
        .catch((error) => {
          console.error('Failed to clear session cookie:', error);
        });
      const auth = getAuth();
      signOut(auth).then(() => {
        commit('SET_CURRENT_USER', null);
        commit('SET_USER_LOGGED', false);
        commit('SET_USER_TOKEN', null);
      }).catch(({ code, message }) => {
        const error = findError(code);
        commit('SET_LOGIN_ERROR', { code, message, error: error?.key });
        commit('SET_USER_LOGGED', false);
        commit('SET_CURRENT_USER', null);
      });
    },
    setShowSchoolGroupDialog({ commit }, payload) {
      commit('SHOW_SCHOOL_GROUP_DIALOG', payload);
    },
    async updateUser({ commit, state, dispatch }, { schoolGroupId }) {
      commit('SET_LOADING', true);
      const { currentUser } = state;
      if (!currentUser) {
        return;
      }
      await firebaseService.updateUser({
        ...currentUser,
        schoolGroupId,
      })
        .then((updatedUser) => {
          commit('SET_CURRENT_USER', updatedUser);
        })
        .catch((error) => {
          const errorCode = error.code;
          const errorMessage = error.message;
          const errorKey = findError(errorCode);
          commit('SHOW_SCHOOL_GROUP_DIALOG', true);
          dispatch('snackbar/showError', { errorCode, errorMessage, error: errorKey?.key }, { root: true });
        });
      commit('SET_LOADING', false);
    },
  },
  getters: {
    currentUserEmail: (state: any): string | null => state.currentUser?.email
      || state.common?.currentUser?.email
      || null,
  },
  modules: {
    enrollment,
    common,
    snackbar,
    versionUpdate,
  },
  plugins: [vuexPersist.plugin],
});
